import React, { FC, useEffect, useState, useRef } from 'react';
import {
	Box,
	Typography,
	Avatar,
	Paper,
	CircularProgress,
	IconButton,
	Button,
	TextField,
	Divider,
	Tooltip,
	Snackbar,
	Card,
	Grid,
	Skeleton,
	Dialog,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import LeadForm from './LeadForm';
import ThankYouCard from './ThankYouCard';
import { useGlobalState } from '../GlobalStateContext';
//import CodsterLogo from './images/codster_logo.jpeg';
import NotFound from './../Assets/Images/404.png';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useSpeechSynthesis } from '../Hooks/useSpeechSynth';
import textToVoice from './Api/textToVoice';
import { sendFeedback } from './Api/feedback';
import Suggestions from './Suggestions';
import sanitizeTextForSpeech from '../Hooks/sanitizeTextForSpeech';

import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AnimatedMarkdown, { SimpleMarkdown, StreamingMarkdown } from './TypingMarkdown';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import HubspotForm from './HubspotFormComponent/HubspotFormComponent';
import { ConnectWithoutContact } from '@mui/icons-material';

interface MessageProps {
	message: {
		id: string;
		text: string;
		active_form: boolean;
		sender: string;
		traceId?: string;
	};
	conversationId: string | null;
	isLoading: boolean;
	isChatLoading: boolean;
	botMessageBgColor: string;
	userMessageBgColor: string;
	botMessageColor: string;
	userMessageColor: string;
	onTypingStatusChange: (newStatus: string) => void;
	formAlreadySent: boolean;
	setFormAlreadySent: (value: boolean) => void;
	setSuggestionSelected: (selected: string) => void;
	appId: string;
	organizationId: string;
	time: string;
	showIconVolume: boolean;
	typingStatus: string;
	shortcutsEnabled: boolean;
	isStreaming?: boolean;
	link?: string;
	showLink?: boolean;
	setShowContactForm: (value: boolean) => void;
}

const Message: FC<MessageProps> = ({
	message,
	conversationId,
	isLoading,
	isChatLoading,
	botMessageBgColor,
	userMessageBgColor,
	botMessageColor,
	userMessageColor,
	onTypingStatusChange,
	formAlreadySent,
	setFormAlreadySent,
	setSuggestionSelected,
	appId,
	organizationId,
	showIconVolume,
	typingStatus,
	shortcutsEnabled,
	isStreaming = false,
	setShowContactForm,
}) => {
	const { state: globalState } = useGlobalState();
	const chatbotSettings = globalState.chatbotSettings;

	const [feedbackType, setFeedbackType] = useState<'up' | 'down' | null>(null);
	const [showLoader, setShowLoader] = useState(false);
	const [showForm, setShowForm] = useState(true);
	const [showThankYou, setShowThankYou] = useState(false);
	const [showFormattedText, setShowFormattedText] = useState(false);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [suggestions, setSuggestions] = useState([]);
	const isBot = message.sender === 'bot';
	const isActiveForm = message.active_form === true;
	const [hasFeedback, setHasFeedback] = useState(false);
	const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
	const [customFeedback, setCustomFeedback] = useState('');
	const [showCustomInput, setShowCustomInput] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
	const [wasStreamed, setWasStreamed] = useState(isStreaming);
	const [link, setLink] = useState('');
	const [showLink, setShowLink] = useState(false);

	useEffect(() => {
		if (isStreaming) {
			setWasStreamed(true);
		}
	}, [isStreaming]);

	const getSuggestions = (text: any) => {
		const patron = /(?:"|')?suggestions(?:"|')?\s*:\s*\[(.*?)\]/s;
		const matchFound = text.match(patron);

		if (matchFound && matchFound[1]) {
			const content = matchFound[1];
			const suggestionsExtracted = content.match(/(['"])(.*?)\1/g) || [];
			const finalSuggestions = suggestionsExtracted.map((s: string) => s.slice(1, -1));
			setSuggestions(finalSuggestions);
			return finalSuggestions;
		}
		return [];
	};

	const handleSuggestionClick = async (suggestionText: string) => {
		setSuggestionSelected(suggestionText);
	};

	const formatText = (text: string) => {
		const urlPattern = /(https?:\/\/[^\s]+)/g;
		const imgPattern = /(https?:\/\/.*\.(?:png|jpg|gif|jpeg|webp))/i;
		const gdPattern = /https:\/\/drive\.google\.com\/file\/d\/(.*?)\/.*?/;
	
		const patron = /['"]?suggestions['"]?\s*:?\s*\[[\s\S]*?\]/gi;
		const cleanText = text.replace(patron, '');
	
		return cleanText.split(urlPattern).map((part, index) => {
			if (part.match(imgPattern)) {
				return `![image](${part})\n`;
			} else if (gdPattern.test(part)) {
				const match = part.match(gdPattern);
				const fileId = match?.[1];
				const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
				return `<iframe src="${embedUrl}" width="100%" height="300" allow="autoplay"></iframe>\n`;
			} else if (part.match(urlPattern)) {
				return `[${part}](${part})`;
			}
			return part;
		}).join('');
	};

	useEffect(() => {
		if (!isBot) {
			setShowFormattedText(true);
		}
	}, [isBot]);

	const handleLeadFormSubmit = () => {
		setShowThankYou(true);
	};
	
	const [isPlayingAudio, setIsPlayingAudio] = useState(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	const playAudio = async () => {
		try {
			setIsPlayingAudio(true);
			const newMessage = sanitizeTextForSpeech(message.text);
			const response = await textToVoice(newMessage, appId);
	
			const byteCharacters = atob(response.audio_base64);
			const byteNumbers = new Array(byteCharacters.length);
			for (let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			const blob = new Blob([byteArray], { type: `audio/${response.output_format}` });
			const url = URL.createObjectURL(blob);
	
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
	
			audioRef.current = new Audio(url);
			audioRef.current.onended = () => {
				setIsPlayingAudio(false);
			};
			await audioRef.current.play();
		} catch (err) {
			console.error(err);
			setIsPlayingAudio(false);
			setSnackbarMessage(err instanceof Error ? err.message : 'Error al reproducir el audio');
			setSnackbarSeverity('error');
			setSnackbarOpen(true);
		}
	};

	const stopAudio = () => {
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
			setIsPlayingAudio(false);
		}
	};

	const handleLeadFormClose = () => {
		setShowForm(false);
	};

	useEffect(() => {
		if (message.sender === 'user') {
			setShowLoader(true);
		}
	}, [message]);

	useEffect(() => {
		if (message.sender === 'bot'){
			const url = findValidUrl(message.text);
			if (url && !showLink) {
				setLink(url);
				setShowLink(true);
			}
		}
	}, [message]);

	useEffect(() => {
		if (!isLoading) {
			setShowLoader(false);
		}
	}, [isLoading]);
	

	const { speak, pause, resume, cancel, isSpeaking, isPaused } =
		useSpeechSynthesis({
			volume: 0.8,
			rate: 1,
			pitch: 1,
			lang: 'es-ES',
		});

	const handleLike = async (isLike: boolean, comment?: string) => {
		if (!message.traceId) return;
		
		try {
			setHasFeedback(true);
			if (isLike) {
				setShowFeedbackOptions(false);
			}
			await sendFeedback(appId, message.traceId, isLike, comment);
			
			setSnackbarMessage('¡Gracias por tu retroalimentación!');
			setSnackbarSeverity('success');
			setSnackbarOpen(true);
		} catch (error) {
			console.error('Error sending feedback:', error);
			setHasFeedback(false);
			setFeedbackType(null);
			setShowFeedbackOptions(false);
			setCustomFeedback('');
			setShowCustomInput(false);
			setSnackbarMessage('Lo sentimos, hubo un error al enviar tu retroalimentación. Por favor, intenta de nuevo.');
			setSnackbarSeverity('error');
			setSnackbarOpen(true);
		}
	};

	const handleFeedbackOption = (option: string) => {
		setFeedbackType('down');
		handleLike(false, option);
		setShowCustomInput(false);
		setHasFeedback(true);
	};

	const findValidUrl = (text: string) => {
		const urlRegex = /(https?:\/\/[^\s]+)/g;
		const matches = text.match(urlRegex);
		return matches ? matches[0] : null;
	};

	const userInputStyle = {
		color: '#212121',
		fontSize: '24px',
		fontStyle: 'normal',
		fontWeight: 700,
		lineHeight: 'normal',
	};

	const skeletonLoadingStyle = {
		background: 'linear-gradient(90deg, #E4FEF5 0.02%, #E1F3EE 100.02%)',
		fontSize: '1rem'
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				mb: isBot ? 4 : 2,
				position: 'relative',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-start',
					alignContent: 'flex-start',
					width: '100%',
				}}
			>
				{isBot ? (
					<Box sx={{ flex: '0 0 40px', animationDelay: '2s' }} className='animate__animated animate__fadeIn'>
						<Avatar>
							<img
								src={
									chatbotSettings.image_url &&
								chatbotSettings.image_url !== 'None'
										? chatbotSettings.image_url
										: NotFound
								}
								alt={
									chatbotSettings.display_name &&
								chatbotSettings.display_name !== 'None'
										? chatbotSettings.display_name
										: 'Codster Logo'
								}
								style={{
									width: '100%',
									height: '100%',
									borderRadius: '50%',
									objectFit: 'cover',
								}}
							/>
						</Avatar>
					</Box>
				) : (
					<Box sx={{ flex: '0 0 40px', textAlign: 'center', animationDelay: '2s' }} className='animate__animated animate__fadeInLeft'>
						<ArrowUpwardIcon sx={{ color: '#45CFC8', fontSize: '32px', marginTop: '2px', transform: 'rotate(90deg)'}} />
					</Box>
				)}
				<Box sx={{ flex: isBot ? '0 0 calc(100% - 40px)' : '1 0 calc(100% - 40px)' }}>
					<Paper
						variant="outlined"
						className={`bot-message ${isBot ? 'bot-message' : ''}`}
						sx={{
							p: 0,
							ml: 2,
							mr: 0,
							mt: 0,
							backgroundColor: 'transparent',
							color: '#212121',
							borderRadius: 0,
							position: 'relative',
							border: 'none',
							overflowY: 'auto',
							fontSize: '18px',
							lineHeight: 1.5,
							whiteSpace: 'normal',
							'& p:first-of-type': {
								marginTop: '0',
							}
						}}
					>
						<div style={{ whiteSpace: 'pre-line' }}>
							{isBot ? (
								showFormattedText ? (
									<SimpleMarkdown content={formatText(message.text)}/>
								) : isChatLoading ? (
									<Box className="animate__animated animate__fadeIn" sx={{ animationDelay: '1s', animationDuration: '2s' }}>
										<Skeleton sx={skeletonLoadingStyle} variant="text" animation="wave" />
										<Skeleton sx={skeletonLoadingStyle} variant="text" animation="wave" />
										<Skeleton sx={skeletonLoadingStyle} variant="text" animation="wave" />
										<Skeleton sx={skeletonLoadingStyle} variant="text" animation="wave" />
									</Box>
								) : wasStreamed ? (
									<StreamingMarkdown
										content={formatText(message.text)}
										isStreaming={isStreaming}
										onStreamingStatusChange={(status) => {
											if (status === 'streaming') {
												onTypingStatusChange('Typing');
											} else {
												onTypingStatusChange('Done Typing');
												setShowFormattedText(true);
												getSuggestions(message.text);
												setShowSuggestions(true);
											}
										}}
									/>
								) : (
									<AnimatedMarkdown
										content={formatText(message.text)}
										typingSpeed={60}
										onStart={() => {
											onTypingStatusChange('Typing');
										}}
										onComplete={() => {
											onTypingStatusChange('Done Typing');
											setShowFormattedText(true);
											getSuggestions(message.text);
											setShowSuggestions(true);
										}}
									/>
								)
							) : (
								<Typography sx={{...userInputStyle, animationDelay: '1s'}} className='animate__animated animate__fadeIn'>
									{message.text}
								</Typography>
							)}
						</div>
						{showLoader && isBot && (
							<Box
								sx={{
									position: 'absolute',
									bottom: '4px',
									right: '4px',
								}}
							>
								<div>Loading...</div>
							</Box>
						)}
						{/* {isBot && showLink && link && link !== '' && (
							<Box sx={{ marginBottom: '28px' }}>
								<a
									href={link}
									target='_blank'
									style={{
										textDecoration: 'none'
									}} rel="noreferrer"
								>
									<Card sx={{
										padding: '16px 24px 16px 16px',
										marginTop: '1rem',
										display: 'block'
									}}>
										<Grid container spacing={1} style={{ alignItems: 'center', flexWrap: 'nowrap' }}>
											<Grid item style={{ flexBasis: '48px' }}>
												<Box 
													sx={{
														width: '48px',
														height: '48px',
														position: 'relative',
														backgroundColor: '#DA99FF',
														borderRadius: '8px'
													}}
												>
													<InsertLinkIcon sx={{
														position: 'absolute',
														top: '50%',
														left: '50%',
														transform: 'translate(-50%, -50%)',
														color: '#760AA8'
													}}
												
													></InsertLinkIcon>
												</Box>
											</Grid>
											<Grid item style={{ flexBasis: 'auto' }}>
												<span style={{ 
													display: 'block', 
													width: '100%', 
													maxWidth: '320px',
													overflow: 'hidden', 
													textOverflow: 'ellipsis', 
													whiteSpace: 'nowrap'
												}}> Visita {link}</span>
											</Grid>
										</Grid>
									</Card>
								</a>
							</Box>
						)} */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> 
								{(isBot && showIconVolume && chatbotSettings.is_voice_enabled) && (
									!isPlayingAudio ? (
										<IconButton
											size="small"
											sx={{ padding: '6px' }}
											onClick={playAudio}
										>
											<VolumeUpIcon />
										</IconButton>
									) : ( 
										<IconButton
											size="small"
											sx={{ padding: '6px' }}
											onClick={stopAudio}
										>
											<StopCircleIcon />
										</IconButton>
									)
								)}
								{message.sender === 'bot' && message.traceId && showIconVolume && (
									<div className='animate__animated animate__fadeIn' style={{ animationDelay: '0s' }}>
										<Tooltip title="Respuesta útil">
											<span>
												<IconButton 
													onClick={() => {
														setFeedbackType('up');
														handleLike(true);
													}}
													disabled={hasFeedback}
													sx={{ padding: '6px' }}
													
												>
													{feedbackType === 'up' ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="No fue útil">
											<span>
												<IconButton 
													onClick={() => {
														setShowFeedbackOptions(true);
													}}
													disabled={hasFeedback}
													sx={{ padding: '6px' }}
												>
													{feedbackType === 'down' || showFeedbackOptions ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
												</IconButton>
											</span>
										</Tooltip>
									</div>
								)}
							</Box>
							{showFeedbackOptions && (
								<>
									<Divider />
									<Box sx={{ mt: 1 }}>
										<Typography
											variant="body2"
											sx={{
												mb: 1,
												fontWeight: 700
											}}
										>
											{!hasFeedback ? '¿Qué podríamos mejorar?' : 'Gracias por ayudarnos a seguir mejorando'}
										</Typography>
										{!hasFeedback && (
											<>
												<Box sx={{ 
													display: 'flex', 
													flexWrap: 'wrap',
													gap: 1,
													mb: showCustomInput ? 1 : 0
												}}>
													<Button 
														variant="outlined" 
														size="small"
														sx={{
															borderRadius: '20px',
															textTransform: 'none',
															borderColor: 'grey.400',
															color: 'black',
															bgcolor: 'white',
															'&:hover': {
																borderColor: 'grey.500',
																bgcolor: 'grey.50'
															}
														}}
														onClick={() => handleFeedbackOption('Respuesta incorrecta')}
													>
													Respuesta incorrecta
													</Button>
													<Button 
														variant="outlined" 
														size="small"
														sx={{
															borderRadius: '20px',
															textTransform: 'none',
															borderColor: 'grey.400',
															color: 'black',
															bgcolor: 'white',
															'&:hover': {
																borderColor: 'grey.500',
																bgcolor: 'grey.50'
															}
														}}
														onClick={() => handleFeedbackOption('No entendí la respuesta')}
													>
													No entendí la respuesta
													</Button>
													<Button 
														variant="outlined" 
														size="small"
														sx={{
															borderRadius: '20px',
															textTransform: 'none',
															borderColor: 'grey.400',
															color: 'black',
															bgcolor: 'white',
															'&:hover': {
																borderColor: 'grey.500',
																bgcolor: 'grey.50'
															}
														}}
														onClick={() => handleFeedbackOption('Demasiado lento')}
													>
													Demasiado lento
													</Button>
													<Button 
														variant="outlined" 
														size="small"
														sx={{
															borderRadius: '20px',
															textTransform: 'none',
															borderColor: 'grey.400',
															color: 'black',
															bgcolor: showCustomInput ? 'grey.400' : 'white',
															'&:hover': {
																borderColor: 'grey.500',
																bgcolor: 'grey.50'
															}
														}}
														onClick={() => setShowCustomInput(true)}
													>
													Otro
													</Button>
												</Box>
												{showCustomInput && (
													<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
														<TextField
															size="small"
															fullWidth
															placeholder="Comparte tus ideas aquí."
															variant="outlined"
															value={customFeedback}
															onChange={(e) => setCustomFeedback(e.target.value)}
															autoComplete={'off'}
															sx={{ backgroundColor: 'white' }}
															InputProps={{
																endAdornment: (
																	<Button
																		style={{
																			backgroundColor: chatbotSettings.button_color || 'green',
																			color: 'white',
																			width: '2rem',
																			height: '2rem',
																			borderRadius: '50%',
																			minWidth: 'unset',
																			margin: '11px 0px 11px 11px',
																			position: 'relative',
																		}}
																		onClick={() => {
																			if (customFeedback.trim()) {
																				handleFeedbackOption(customFeedback);
																				setCustomFeedback('');
																			}
																		}}
																	>
																		<ArrowUpwardIcon />
																	</Button>
																),
															}}
														/>
													</Box>
												)}
											</>
										)}
									</Box>
								</>
							)}
						</Box>			
						{showLoader && isBot && (
							<Box
								sx={{
									position: 'absolute',
									bottom: '4px',
									right: '4px',
								}}
							>
								<CircularProgress size={16} />
							</Box>
						)}
					</Paper>
				</Box>
			</Box>
			{(isBot && suggestions.length > 0 && showSuggestions) && (
				<Box 
					sx={{
						display: 'flex',
						flexDirection: 'row',
						p: 2,
						ml: 2,
						mr: 0,
						borderRadius: '5px 20px 20px 10px',
						maxWidth: '100%',
						position: 'relative',
						border: 'none',
						overflowY: 'auto',
					}}
				>
					<Suggestions
						suggestions={suggestions}
						onSuggestionClick={handleSuggestionClick}
						suggestionsEnabled={
							shortcutsEnabled &&
							typingStatus === 'Done Typing' &&
							!isChatLoading
						}
					/>
				</Box>
			)}
			{!formAlreadySent &&
				isBot &&
				showForm &&
				!showThankYou &&
				isActiveForm &&
				conversationId &&
				appId &&
				organizationId && (
				<Box sx={{ paddingLeft: '60px', marginTop: '16px' }}>
					<Button 
						onClick={() => setShowContactForm(true)} 
						variant="contained"
						startIcon={<ConnectWithoutContact />}
						disableElevation={true}
						disableFocusRipple={true}
						sx={{ 
							borderRadius: '16px',
							backgroundColor: 'rgb(118, 10, 168)',
							color: 'white',
							textTransform: 'inherit',
							fontWeight: 700,
							fontSize: '14px',
							'&:hover': {
								backgroundColor: 'rgb(118, 10, 168)',
								color: 'white',
							}
						}}
					>
						Contáctanos y conoce más
					</Button>
				</Box>
				// <LeadForm
				// 	onSubmit={handleLeadFormSubmit}
				// 	conversationId={conversationId}
				// 	onClose={handleLeadFormClose}
				// 	setFormAlreadySent={setFormAlreadySent}
				// 	appId={appId}
				// 	organizationId={organizationId}
				// />
			)}
			{showThankYou && isBot && (
				<ThankYouCard onClose={() => setShowForm(false)} />
			)}
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={2000}
				onClose={() => setSnackbarOpen(false)}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity={snackbarSeverity}
					onClose={() => setSnackbarOpen(false)}
				>
					{snackbarMessage}
				</MuiAlert>
			</Snackbar>
			
		</Box>
	);
};

export default Message;
